import {
  CalendarClock,
  Cloud,
  FileCheck,
  FolderKanban,
  Hourglass,
  Inbox,
  Library,
  Scale,
  Zap,
  type LucideIcon,
} from "lucide-react";

import type { BeadType } from "@domain/praxis-beads";

// Icon + tone derive from the bead TYPE here in the component layer —
// lucide/react framework imports stay out of `domain/` (spec §3.5). Each
// tone carries its `dark:` pair (frontend-doctrine §5.4). Shared by the
// list row and the detail view.
export const TYPE_PRESENTATION: Readonly<
  Record<BeadType, { icon: LucideIcon; tone: string }>
> = {
  inbox: {
    icon: Inbox,
    tone: "bg-home-ink/[0.06] dark:bg-white/[0.06] text-home-ink/70 dark:text-white/70",
  },
  "next-action": {
    icon: Zap,
    tone: "bg-home-sky/25 dark:bg-white/5 text-home-water",
  },
  project: {
    icon: FolderKanban,
    tone: "bg-home-peri/30 dark:bg-white/5 text-[#5B4F9E] dark:text-home-peri",
  },
  "waiting-for": {
    icon: Hourglass,
    tone: "bg-home-butter dark:bg-white/5 text-home-oxblood dark:text-home-butter",
  },
  someday: {
    icon: Cloud,
    tone: "bg-home-ink/[0.06] dark:bg-white/[0.06] text-home-ink/60 dark:text-white/60",
  },
  reference: {
    icon: Library,
    tone: "bg-home-candy/15 dark:bg-home-candy/10 text-home-candy",
  },
  decision: {
    icon: Scale,
    tone: "bg-home-yellow/50 dark:bg-white/5 text-home-ink dark:text-home-butter",
  },
  commitment: {
    icon: CalendarClock,
    tone: "bg-home-coral/15 dark:bg-home-coral/10 text-home-coral",
  },
  output: {
    icon: FileCheck,
    tone: "bg-accent-green/15 dark:bg-accent-green/10 text-accent-green",
  },
};

/** Human label for a bead type on the detail page's meta rows. */
export const TYPE_LABEL: Readonly<Record<BeadType, string>> = {
  inbox: "Inbox",
  "next-action": "Next action",
  project: "Project",
  "waiting-for": "Waiting for",
  someday: "Someday",
  reference: "Reference",
  decision: "Decision",
  commitment: "Commitment",
  output: "Output",
};

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Coarse relative time for row/detail meta lines. Presentation-only. */
export function relativeTime(from: number, now: number): string {
  const delta = Math.max(0, now - from);
  if (delta < MINUTE) return "just now";
  if (delta < HOUR) return `${Math.floor(delta / MINUTE)}m ago`;
  if (delta < DAY) return `${Math.floor(delta / HOUR)}h ago`;
  return `${Math.floor(delta / DAY)}d ago`;
}
